
import React from 'react';
import { Badge as BadgeType } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Award, Trophy, Star, Sparkles, Users, GraduationCap, Flame } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface BadgeDisplayProps {
  badges: BadgeType[];
  limit?: number;
  showAll?: boolean;
  compact?: boolean; // Add the missing compact prop
}

const BadgeDisplay: React.FC<BadgeDisplayProps> = ({ 
  badges, 
  limit = 5,
  showAll = false,
  compact = false // Add default value for the prop
}) => {
  const displayBadges = showAll ? badges : badges.slice(0, limit);
  const remaining = badges.length - limit;
  
  const getBadgeIcon = (category: string) => {
    switch (category) {
      case 'achievement':
        return <Trophy className="h-3 w-3" />;
      case 'milestone':
        return <Star className="h-3 w-3" />;
      case 'collaboration':
        return <Users className="h-3 w-3" />;
      case 'learning':
        return <GraduationCap className="h-3 w-3" />;
      case 'special':
        return <Sparkles className="h-3 w-3" />;
      default:
        return <Award className="h-3 w-3" />;
    }
  };
  
  const getBadgeVariant = (rarity: string) => {
    switch (rarity) {
      case 'common':
        return 'default';
      case 'uncommon':
        return 'secondary';
      case 'rare':
        return 'soundcharts';
      case 'epic':
        return 'neon-purple';
      case 'legendary':
        return 'cyber';
      default:
        return 'default';
    }
  };
  
  return (
    <div>
      {!compact && <h4 className="text-sm font-medium mb-2">Artist Badges</h4>}
      <div className="flex flex-wrap gap-2">
        <TooltipProvider>
          {displayBadges.map((badge) => (
            <Tooltip key={badge.id}>
              <TooltipTrigger asChild>
                <Badge 
                  variant={getBadgeVariant(badge.rarity)}
                  className="cursor-help flex items-center gap-1"
                >
                  {getBadgeIcon(badge.category)}
                  <span>{badge.name}</span>
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <div className="space-y-1 p-1">
                  <p className="font-medium">{badge.name}</p>
                  <p className="text-xs">{badge.description}</p>
                  <p className="text-xs text-muted-foreground">
                    Earned on {badge.dateEarned.toLocaleDateString()}
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>
          ))}
          
          {!showAll && remaining > 0 && (
            <Badge variant="outline" className="cursor-pointer">
              +{remaining} more
            </Badge>
          )}
        </TooltipProvider>
      </div>
    </div>
  );
};

export default BadgeDisplay;
